import { cn } from "@/lib/utils";

/**
 * Tier indicator chip. Five-step palette pulled from the extended status
 * tokens (teal / purple / orange with light variants) so the visual
 * hierarchy is intuitive: T1 is the heaviest weight, T5 the most relaxed.
 *
 *   T1 → teal solid    (top-tier revenue, attention-grabbing)
 *   T2 → teal light    (large but quieter)
 *   T3 → purple solid  (mid book)
 *   T4 → purple light  (smaller mid)
 *   T5 → orange light  (long-tail accounts)
 *
 * Used in the Renewals pipeline and any other table that surfaces tier.
 * Reach for `<TierChip tier={r.current_tier} />` rather than rolling your own
 * tier coloring — keeps the palette consistent across the app.
 */
export function TierChip({
  tier,
  className,
}: {
  /** 1..5; null/undefined renders an em-dash placeholder. */
  tier: number | null | undefined;
  className?: string;
}) {
  if (tier == null) {
    return <span className={cn("text-muted-foreground", className)}>—</span>;
  }
  const styles = TIER_STYLES[tier] ?? TIER_STYLES[5];
  return (
    <span
      className={cn(
        "inline-flex h-6 min-w-[2.25rem] items-center justify-center rounded-md border px-2 text-xs font-bold uppercase tracking-wider",
        styles,
        className,
      )}
    >
      T{tier}
    </span>
  );
}

const TIER_STYLES: Record<number, string> = {
  1: "border-transparent bg-teal text-white",
  2: "border-transparent bg-teal-light text-teal",
  3: "border-transparent bg-purple text-white",
  4: "border-transparent bg-purple-light text-purple",
  5: "border-transparent bg-orange-light text-orange",
};
